/* eslint-env browser, jquery */

'use strict';

const { windowLoaded } = require('@grpn/document-states');

const Media = require('./Media');
const traitsMatch = require('../utils/traits_match');

class MediaContainer {
  constructor(data, $el, onLoadAction, postSwapActions) {
    this.data = data;
    this.index = 0;
    this.$mediaWrapper = $el.querySelector('.media-container');
    this.$featuredImg = this.$mediaWrapper.querySelector('img.current-media');
    this.$featuredImg.setAttribute('onload', onLoadAction);
    this.$waitingImages = this.$mediaWrapper.querySelectorAll('.waiting-image');
    this.onBlur = this.onBlur.bind(this);

    this.mediaArray = [];
    this.mediaCount = data.media.length;
    for (let i = 0; i < this.mediaCount; i += 1) {
      this.mediaArray.push(new Media(data.media[i], this.$mediaWrapper));
    }
    this.currentMedia = this.mediaArray[this.index];

    const basePostSwapActions = {
      preload: this.preloadMedia,
    };

    if (this.data.imageZoom) {
      Object.assign(basePostSwapActions, {
        setUpZoom: nextMediaObj => this.setUpZoom(nextMediaObj),
      });
    }
    this.basePostSwapActions = {
      ...basePostSwapActions,
      ...postSwapActions,
    };

    this.$zoomMessage = $el.querySelector('.image-zoom-message');

    this.videoLoader($el, data);
    /*
      fallback urls use the same image url for thumbnails and main image;
      Safari won't trigger the onload event for <img>
        when new src is the same as the current, loaded source.
    */
    if (data.media[0].fallbackUrls) {
      // use setTimeout so that onload event will happen after DealCarousel returns
      setTimeout(() => this.$featuredImg.dispatchEvent(new Event('onload')), 0);
    }
  }

  videoLoader($el, data) {
    windowLoaded.then(() =>
      this.fetchVideoScripts(
        data.videoScriptUrls,
        $el.querySelectorAll('.video-container, .thumbnail.video')
      )
    );
  }

  fixHeight(height, width) {
    Object.assign(this.$mediaWrapper.style, {
      height: `${height}px`,
      width: `${width}px`,
      'padding-top': 0,
    });
    this.$mediaWrapper
      .querySelectorAll('.video-container .video-player')
      .forEach(el => Object.assign(el.style, { height: `${height}px` }));
  }

  onLoad() {
    if (this.$mediaWrapper.classList.contains('loaded')) {
      return;
    }
    this.$waitingImages.forEach(element =>
      element.setAttribute('src', element.dataset.thumb)
    );

    this.$mediaWrapper.classList.add('loaded');
    Object.assign(this.$featuredImg.style, {
      filter: 'blur(0px)',
      '-webkit-filter': 'blur(0px)',
    });
    this.$featuredImg.dataset.loaded = 'true';
  }

  onBlur() {
    if (document.activeElement instanceof HTMLIFrameElement) {
      window.removeEventListener('blur', this.onBlur, false);
    }
  }

  getActiveId() {
    return this.currentMedia.getId();
  }

  preloadMedia() {
    this.loadMediaAt(this.index - 1);
    this.loadMediaAt(this.index + 1);
  }

  loadMediaAt(index) {
    let adjustedIndex;
    if (index < 0) {
      adjustedIndex = this.mediaCount + index;
    } else if (index >= this.mediaCount) {
      adjustedIndex = 0;
    } else {
      adjustedIndex = index;
    }

    return this.mediaArray[adjustedIndex].load();
  }

  getIndexFromTraits({ selectedTraits }) {
    if (!this.data.hasTraits) {
      return this.index;
    }

    if (Object.keys(selectedTraits).length === 0) {
      return 0;
    }

    const currentImageTraits = this.mediaArray[this.index].data.traits;
    if (currentImageTraits && traitsMatch(selectedTraits, currentImageTraits)) {
      return this.index;
    }

    for (let i = 0; i < this.mediaCount; i += 1) {
      if (!this.mediaArray[i].data.isVideo) {
        const imageTraits = this.mediaArray[i].data.traits;
        if (!imageTraits || imageTraits.length === 0) continue;

        if (traitsMatch(selectedTraits, imageTraits)) {
          return i;
        }
      }
    }
    return 0;
  }

  getIndexByDataId({ dataId }) {
    return this.mediaArray.findIndex(media => media.getId() === dataId);
  }

  getMediaByDataId(dataId) {
    return this.mediaArray.find(media => media.getId() === dataId);
  }

  incrementIndex() {
    return this.index + 1 === this.mediaCount ? 0 : this.index + 1;
  }

  decrementIndex() {
    return this.index - 1 === -1 ? this.mediaCount - 1 : this.index - 1;
  }

  getTraitsByIndex() {
    return this.mediaArray[this.index].data.traits;
  }

  fetchVideoScripts(videoScriptUrls, $videoEls) {
    if (videoScriptUrls) {
      if (!window.YT && videoScriptUrls.youtubeScriptUrl) {
        const script = document.createElement('script');
        script.id = 'carousel-yt';
        script.onerror = () => {
          $videoEls && $videoEls.length > 0
            ? $videoEls.forEach(el =>
                el && el.parentNode ? el.parentNode.removeChild(el) : null
              )
            : undefined;
        };

        script.src = videoScriptUrls.youtubeScriptUrl;
        (document.body || document.head).appendChild(script);
      }
    }
  }

  swapMedia(newMediaObj) {
    this.currentMedia.hide();
    newMediaObj.show();
    this.currentMedia = newMediaObj;
  }

  setUpZoom(newMediaObj) {
    if (!this.$zoomMessage) {
      return;
    }
    if (newMediaObj.setUpZoom()) {
      this.$zoomMessage.classList.remove('hide-zoom');
    } else {
      this.$zoomMessage.classList.add('hide-zoom');
    }
  }

  handleMediaSwap(indexFinder, data = {}, postSwapActions = {}) {
    const newIndex = this[indexFinder](data);
    if (newIndex === this.index || newIndex === undefined || newIndex === -1)
      return;

    const nextMediaObj = this.loadMediaAt(newIndex);
    // bail out of swap if loadMediaAt fails
    if (!nextMediaObj) return;

    this.index = newIndex;
    this.swapMedia(nextMediaObj);

    const actionsHash = {
      ...this.basePostSwapActions,
      ...postSwapActions,
    };
    const actionNames = Object.keys(actionsHash);

    for (let i = 0; i < actionNames.length; i += 1) {
      const action = actionsHash[actionNames[i]];
      if (action !== undefined) {
        action.call(this, nextMediaObj, data);
      }
    }
  }
}

module.exports = MediaContainer;
